// ABOUTME: SPEC-52 D-13 — the waitlist request panel. Chrome borrowed verbatim from ConsentGate
// ABOUTME: (scrim/card/type floor) and dismissal from PrivacyPolicy (X, Escape, backdrop) — no
// ABOUTME: new modal chrome invented (owner instruction).
//
// Three states, driven entirely by `useWaitlist.js`: `form` (the four fields + variant chips),
// `submitting` (fields disabled, no dismiss race with the in-flight send), `confirmed` (no X, no
// Escape, no backdrop dismiss — nothing left to cancel — auto-closes after CONFIRM_DWELL_MS).
//
// Voice "never mind" and the X button are the same code path (D-14): the agent's
// `dismiss_waitlist_form` arrives as the same `{open: false}` server message `useWaitlist.js`'s
// `handleServerMessage` already routes to `close()`, so there is only ever one exit.

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import WaitlistFields from './WaitlistFields';
import {
  FIELD, MOBILE_FIELD, CHIP, MOBILE_CHIP,
  CLOSE_BOX_AUTHORED, CLOSE_BOX_MIN_PX, CLOSE_GLYPH_AUTHORED, CLOSE_GLYPH_MIN_PX,
} from './waitlistGeometry';
import {
  RAIL_UNIT, u, uMin, CONSENT_PANEL, CONSENT_TEXT, PANEL_PAD, PANEL_RADIUS, PANEL_W, PANEL_MAX_VW,
} from '../ReelChrome/railGeometry';
import {
  MOBILE_UNIT, MOBILE_CONSENT, MOBILE_CONSENT_TEXT, MOBILE_PANEL, muMin, MIN_TEXT_PX,
} from '../ReelChrome/mobileGeometry';
import { useBreakpoint } from '../Projectionist/useViewportSize';
import { readDeviceProfile } from '../../utils/deviceProfile';

// Fallbacks for the four UI-only states `config/waitlist.py`'s `modal` mapping does not send
// (`submitting`, `error_name`) plus safety nets for the six it does — merged UNDER whatever the
// server sends. Key names are the BACKEND's contract verbatim (`Versai-ophion/config/waitlist.py:91-104`,
// emitted as-is by `services/system_service.py:90`): snake_case, because the backend owns this
// shape and its own tests assert it. `__tests__/WaitlistModal.test.js` pins DEFAULT_COPY as a
// superset of that mapping so the two cannot silently drift apart again (review finding BLOCKER 1,
// 2026-09-10 — six of nine keys were camelCase and silently dropped every tenant string).
export const DEFAULT_COPY = {
  title: 'Join the waitlist',
  body: 'This piece is not available to purchase yet — leave your details and the house will be in touch.',
  name_label: 'Name', email_label: 'Email', phone_label: 'Phone', message_label: 'Message',
  submit_label: 'Submit',
  confirmation: "You're on the list — the house will be in touch.",
  error_email: 'Enter a valid email address',
  // Frontend-only states — never sent by the backend, so no drift risk from these two.
  submitting: 'Sending…',
  error_name: 'Enter your name',
  // VER-89 — tenant-authored (config/waitlist.py), with these as the offline fallback.
  error_submit: "That didn't reach us. Please try again in a moment.",
  retry_label: 'Try again',
};

// Everything the guest can reach with a keyboard, in DOM order.
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

function CloseButton({ onClose, unitMin }) {
  const closeBox = unitMin(CLOSE_BOX_AUTHORED, CLOSE_BOX_MIN_PX);
  const closeGlyph = unitMin(CLOSE_GLYPH_AUTHORED, CLOSE_GLYPH_MIN_PX);
  return (
    <button
      type="button" onClick={onClose} aria-label="Close waitlist form"
      className="absolute z-10 flex items-center justify-center rounded-full bg-reel-chrome-iconChip text-reel-chrome-textPrimary hover:opacity-70 transition-opacity reel-touch-target"
      style={{
        top: `calc(${closeBox} * -1)`,
        right: `calc(-1 * min(${closeBox}, (100vw - 100%) / 2))`,
        width: closeBox, height: closeBox,
      }}
    >
      <X aria-hidden="true" style={{ width: closeGlyph, height: closeGlyph }} />
    </button>
  );
}

function FormBody({ waitlist, copy, geometry, fieldGeometry, chipGeometry, type }) {
  const { collection, fields, errors, variants, selected, setField, toggleVariant, submit, state } = waitlist;
  return (
    <>
      {collection && (
        <p className="font-reel-body uppercase tracking-widest text-reel-chrome-textDim" style={{ fontSize: type(geometry.rowBody ?? CONSENT_TEXT.link), marginBottom: u(16) }}>
          {collection}
        </p>
      )}
      <p className="font-reel-body text-reel-chrome-textDim leading-relaxed" style={{ fontSize: type(CONSENT_TEXT.rowBody), marginBottom: u(32) }}>
        {copy.body}
      </p>
      <WaitlistFields
        fields={fields} errors={errors} onFieldChange={setField}
        variants={variants} selected={selected} onToggleVariant={toggleVariant}
        copy={copy} geometry={fieldGeometry} chipGeometry={chipGeometry}
        unit={u} type={type} disabled={state === 'submitting'}
      />
      {/* VER-89: the message belongs beside the action that failed, not wedged between the title
          and the eyebrow where it split the header block. And the CTA itself becomes the retry —
          it is still enabled and already calls `submit`, so a separate "try again" link was a
          duplicate control in a vocabulary (a bare underline) this panel uses nowhere else. */}
      {state === 'error' && (
        <p
          role="alert"
          className="font-reel-body text-reel-chrome-textPrimary leading-relaxed"
          style={{ fontSize: type(CONSENT_TEXT.rowBody), marginBottom: u(12) }}
        >
          {copy.error_submit}
        </p>
      )}

      <button
        type="button" onClick={submit} disabled={state === 'submitting'}
        className="w-full uppercase bg-reel-cta-primaryBg text-reel-chrome-textInk font-reel-body flex items-center justify-center text-center leading-tight hover:opacity-90 transition-opacity disabled:opacity-60"
        style={{ minHeight: u(geometry.footerH), borderRadius: u(geometry.rowRadius), fontSize: type(CONSENT_TEXT.cta), marginTop: u(geometry.footerGap) }}
      >
        {state === 'submitting' ? copy.submitting
          : state === 'error' ? copy.retry_label
          : copy.submit_label}
      </button>
    </>
  );
}

/** @param {ReturnType<import('./useWaitlist').useWaitlist>} waitlist */
export default function WaitlistModal(waitlist) {
  const { open, state, close, modalCopy } = waitlist;
  const isMobile = useBreakpoint() === 'mobile';
  const dismissible = state !== 'confirmed';  // `error` stays dismissible — there IS something to cancel

  useEffect(() => {
    if (!open || !dismissible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismissible, close]);

  // VER-89: `aria-modal="true"` promises focus cannot escape, and nothing was enforcing it — a
  // keyboard or screen-reader user could Tab straight into the page behind the scrim.
  const panelRef = useRef(null);
  const restoreFocusTo = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusTo.current = document.activeElement;
    // Prefer the first FIELD, not merely the first focusable: the close X is first in DOM order,
    // so "first focusable" opened a form with the caret on Dismiss (observed live on dev). Falls
    // back to any focusable — the confirmed state has no inputs — and then to the panel itself,
    // which carries tabIndex={-1} so focus can never land back on the document.
    // Touch devices (prod 2026-10-14) take the panel, never a field: the form is usually opened by
    // the voice agent, not a tap, and iOS Safari focuses a field without a keyboard, after which a
    // tap on that already-focused field raised none (iPhone 13 Pro); Android popped one unasked
    // over half the panel. The keyboard now opens on the guest's own tap; focus stays trapped.
    const fieldFirst = !readDeviceProfile().touch;
    const target = (fieldFirst && panelRef.current?.querySelector('input, textarea'))
      || (fieldFirst && panelRef.current?.querySelector(FOCUSABLE))
      || panelRef.current;
    target?.focus?.();
    return () => {
      const target = restoreFocusTo.current;
      if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
    };
  }, [open]);

  const onPanelKeyDown = (e) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    // JSDOM does not move focus on Tab, so the wrap is driven explicitly at both ends rather
    // than only intercepting the edge — which also makes the behaviour identical in a browser.
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  if (!open) return null;

  const copy = { ...DEFAULT_COPY, ...(modalCopy || {}) };
  const unitCss = isMobile ? MOBILE_UNIT : RAIL_UNIT;
  const geometry = isMobile ? MOBILE_CONSENT : CONSENT_PANEL;
  const text = isMobile ? MOBILE_CONSENT_TEXT : CONSENT_TEXT;
  const panelPad = isMobile ? MOBILE_PANEL.pad : PANEL_PAD;
  const panelRadius = isMobile ? MOBILE_PANEL.radius : PANEL_RADIUS;
  const panelWidthCss = isMobile ? u(MOBILE_PANEL.w) : `min(${u(PANEL_W)}, ${PANEL_MAX_VW}vw)`;
  const unitMin = isMobile ? muMin : uMin;
  const type = (authored) => `max(${((MIN_TEXT_PX * authored) / text.rowBody).toFixed(3)}px, ${u(authored)})`;

  // Portaled to `document.body` — exactly PrivacyPolicy.js's fix for the same bug (its own
  // comment, `:258-267`): `position: fixed` opens its own stacking context, so a `z-[205]` set
  // from inside the app root only out-ranks siblings WITHIN that root. The footer/mic bar and
  // point-tag overlays mount through `OverlayPortal` straight onto `document.body`, so without
  // this portal they paint on top of the modal regardless of z-index (review finding BLOCKER 2,
  // 2026-09-10, confirmed live: opened mid-reel with point-tags up, the modal did not sit
  // cleanly on top). The full-bleed `pointer-events-auto` backdrop below still blocks every
  // click/tap reaching whatever is behind it once the two share this stacking context.
  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label={copy.title}
      onClick={() => dismissible && close?.()}
      style={{
        '--u': unitCss,
        // Same safe-area treatment as PrivacyPolicy/ConsentGate's reel-chrome branch, so the
        // panel never sits under a notch or a home indicator on a portaled full-bleed backdrop.
        paddingTop: 'calc(1rem + var(--reel-safe-t))',
        paddingBottom: 'calc(1rem + var(--reel-safe-b))',
        paddingLeft: 'calc(1rem + var(--reel-safe-l))',
        paddingRight: 'calc(1rem + var(--reel-safe-r))',
      }}
      className="fixed inset-0 z-[205] bg-black/60 backdrop-blur-md flex items-center justify-center pointer-events-auto"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-reel-chrome-panel backdrop-blur-reel-chrome border border-white/10 shadow-2xl flex flex-col"
        style={{ width: panelWidthCss, maxHeight: '85%', borderRadius: u(panelRadius), padding: u(panelPad) }}
      >
        {dismissible && <CloseButton onClose={close} unitMin={unitMin} />}

        {/* PrivacyPolicy.js:123 — scrolling belongs on an INNER container, never on the panel
            that anchors the close button. `overflow-y-auto` forces `overflow-x` to compute to
            `auto` as well, and the X is deliberately offset outside the panel's corner, so
            putting the two on one element clipped the X away entirely: visible to JSDOM and to
            getBoundingClientRect, but elementFromPoint at its own centre returned the backdrop
            (VER-89, observed on dev 2026-09-14). `min-h-0` lets this shrink inside the flex
            column instead of pushing the panel past its own max-height. */}
        <div className="overflow-y-auto overscroll-contain min-h-0">
          <h2 className="font-reel-heading font-extrabold text-reel-chrome-textPrimary" style={{ fontSize: type(text.title), marginBottom: u(24) }}>
            {copy.title}
          </h2>

          {state === 'confirmed' ? (
            <p className="font-reel-body text-reel-chrome-textDim leading-relaxed" style={{ fontSize: type(text.rowBody) }}>
              {copy.confirmation}
            </p>
          ) : (
            <FormBody
              waitlist={waitlist} copy={copy} geometry={geometry}
              fieldGeometry={isMobile ? MOBILE_FIELD : FIELD} chipGeometry={isMobile ? MOBILE_CHIP : CHIP}
              type={type}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
